import React, { useEffect, useState } from 'react';
import { CharacterTradingCard } from './CharacterTradingCard';
import { LibraryItem } from './types';
import { UniverseCommunity, UniverseSquad } from './universeTypes';
import { UniverseRegion } from './universeRegions';
import { communityTransitionName } from './universeTransition';

export function Artwork({ src, className = '' }: { src?: string; className?: string }) {
  const [failed, setFailed] = useState(false);
  const [loaded, setLoaded] = useState(false);
  return src && !failed ? <img className={`${className} ${loaded ? 'art-loaded' : 'art-loading'}`} src={src} alt="" draggable={false} onLoad={() => setLoaded(true)} onError={() => setFailed(true)} /> : <span className={`art-placeholder ${className}`} aria-hidden="true">✦</span>;
}

/* Fit the visible mark, not the white margins baked into many school uploads.
   The source stays untouched; inaccessible pixel data falls back to contain. */
function CommunityLogo({ src }: { src?: string }) {
  const [crop, setCrop] = useState<{ src: string; width: number; height: number; viewBox: string }>();
  useEffect(() => {
    if (!src) return;
    let cancelled = false;
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        const scale = Math.min(1, 320 / Math.max(image.naturalWidth, image.naturalHeight));
        const width = canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
        const height = canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
        const context = canvas.getContext('2d');
        if (!context) return;
        context.drawImage(image, 0, 0, width, height);
        const { data } = context.getImageData(0, 0, width, height);
        let left = width, top = height, right = -1, bottom = -1;
        for (let y = 0; y < height; y++) for (let x = 0; x < width; x++) {
          const i = (y * width + x) * 4;
          if (data[i + 3] > 32 && Math.min(data[i], data[i + 1], data[i + 2]) < 242) {
            left = Math.min(left, x); right = Math.max(right, x);
            top = Math.min(top, y); bottom = Math.max(bottom, y);
          }
        }
        if (right < left || bottom < top || cancelled) return;
        const padding = Math.max(2, Math.ceil(Math.max(right - left, bottom - top) * .04));
        left = Math.max(0, left - padding); top = Math.max(0, top - padding);
        right = Math.min(width, right + padding + 1); bottom = Math.min(height, bottom + padding + 1);
        setCrop({ src, width, height, viewBox: `${left} ${top} ${right - left} ${bottom - top}` });
      } catch { /* Cross-origin logos still display normally through Artwork. */ }
    };
    image.src = src;
    return () => { cancelled = true; };
  }, [src]);
  return crop?.src === src && crop ? <svg className="community-logo-fit" viewBox={crop.viewBox} aria-hidden="true" preserveAspectRatio="xMidYMid meet"><image href={src} width={crop.width} height={crop.height} /></svg> : <Artwork src={src} />;
}

export function CardMiniature({ card }: { card: LibraryItem }) {
  return <div className="card-miniature" inert aria-hidden="true">
    {card.contentType === 'character' || card.contentType === 'world' ? <CharacterTradingCard item={card} schoolLogoUrl={card.schoolLogoUrl} preview /> : <div className="media-miniature"><strong>{card.title}</strong><span>{card.contentType === 'model_3d' ? '3D model' : card.contentType === 'audio' ? '♫ Audio' : card.contentType === 'movie' ? '▶ Video' : card.contentType.replace('_', ' ')}</span></div>}
  </div>;
}

export function Portal({ community, cards }: { community: UniverseCommunity; cards: LibraryItem[] }) {
  const world = cards.find(card => card.contentType === 'world');
  const previews = cards.slice(0, 3);
  return <div className="universe-portal">
    <div className="universe-portal__rim" style={{ viewTransitionName: communityTransitionName(community.id) } as React.CSSProperties}>
      {world && <Artwork className="universe-portal__world" src={world.displayAssetUrl} />}
      <div className={`universe-card-fan has-${previews.length}`}>{previews.map(card => <CardMiniature card={card} key={card.id} />)}</div>
      <span className="universe-portal__badge"><span className="universe-portal__badge-interior"><CommunityLogo src={community.logoUrl} /></span></span>
    </div>
    <strong>{community.name}</strong><small>{cards.length} {cards.length === 1 ? 'creation' : 'creations'}</small>
  </div>;
}

export function Studio({ squad, cards }: { squad: UniverseSquad; cards: LibraryItem[] }) {
  const subject = `${squad.curriculumLabel} ${squad.displayName}`.toLowerCase();
  const badge = /movie|film/.test(subject) ? 'movie-making' : /entrepreneur|business/.test(subject) ? 'entrepreneurship' : /music/.test(subject) ? 'music' : /video game|game design|gaming/.test(subject) ? 'game-design' : /app|software/.test(subject) ? 'app-development' : 'intro-ai';
  const [rawTitle, rawDay] = squad.displayName.split(' · ');
  const title = rawTitle.replace(/\b20\d{2}\b/g, '').trim();
  const days: Record<string, string> = { wed: 'Wednesdays', weds: 'Wednesdays', mon: 'Mondays', tue: 'Tuesdays', tues: 'Tuesdays', thu: 'Thursdays', thurs: 'Thursdays', fri: 'Fridays', sat: 'Saturdays', sun: 'Sundays' };
  const day = days[rawDay?.toLowerCase()] || rawDay;
  const icon = /movie|film/i.test(title) ? 'film' : /game/i.test(title) ? 'game' : 'spark';
  return <div className="universe-studio">
    <div className="universe-studio__art">
      <div className={`universe-studio__shield universe-studio__badge-sheet badge-${badge}`} aria-hidden="true" />
      <span className="universe-studio__symbol" aria-hidden="true"><svg viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.7">{icon === 'film' ? <><rect x="6" y="5" width="20" height="22" rx="3"/><path d="M11 5v22M21 5v22M6 11h5m-5 10h5m10-10h5m-5 10h5"/></> : icon === 'game' ? <><path d="M9 10h14c5 0 7 15 3 15-3 0-5-5-6-5h-8c-1 0-3 5-6 5-4 0-2-15 3-15Z"/><path d="M11 12v7m-3-3h6m8-2h1m-3 3h1"/></> : <path d="m16 3 4 9 9 4-9 4-4 9-4-9-9-4 9-4Z"/>}</svg></span>
    </div>
    <strong>{title}</strong>{day && <span className="universe-studio__day">{day}</span>}<small>{[squad.season, `${cards.length} creations`].filter(Boolean).join(' · ')}</small>
  </div>;
}

export function Constellation({ region, cards = [] }: { region: UniverseRegion; cards?: LibraryItem[] }) {
  const communities = region.communities.slice(0, 7);
  const points = communities.length === 1 ? [[126, 72]] : communities.length === 2 ? [[75, 55], [192, 100]] : [[44, 45], [120, 25], [192, 66], [258, 42], [224, 136], [135, 152], [65, 119]];
  const arcs = communities.slice(1).map((_, index) => {
    const from = points[region.id === 'chicago' ? 0 : index];
    const to = points[index + 1];
    return `M${from[0] + 22},${from[1] + 22} Q${(from[0] + to[0]) / 2 + 22},${(from[1] + to[1]) / 2 + (region.id === 'chicago' ? -6 : 55)} ${to[0] + 22},${to[1] + 22}`;
  }).join(' ');
  return <div className="region-constellation" style={{ '--region-accent': region.accent } as React.CSSProperties}>
    <div className="region-constellation__marks"><svg viewBox="0 0 340 230" aria-hidden="true"><path d={arcs} /></svg>
      {communities.map((community, index) => <span className={`region-mark region-mark--${index}`} style={{ left: points[index][0], top: points[index][1], viewTransitionName: communityTransitionName(community.id) } as React.CSSProperties} key={community.id}><span className="region-mark__logo"><Artwork src={community.logoUrl} /></span></span>)}
    </div><strong>{region.name}</strong><small>{region.communities.length} communities <span>· {region.cardCount} creations</span></small><span className="region-explore">Explore region →</span>
  </div>;
}
